import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  UserPlus, 
  Trash2, 
  Shield, 
  Mail, 
  Calendar,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Superadmins = () => {
  const { language } = useLanguage();
  const [superadmins, setSuperadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    fetchSuperadmins();
  }, []);

  const fetchSuperadmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/superadmins`);
      setSuperadmins(response.data);
    } catch (error) {
      console.error('Error fetching superadmins:', error);
      toast.error(language === 'es' ? 'Error al cargar superadmins' : 'Error loading superadmins');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newAdmin.email || !newAdmin.password || !newAdmin.name) {
      toast.error(language === 'es' ? 'Todos los campos son obligatorios' : 'All fields are required');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/auth/create-superadmin`, newAdmin);
      toast.success(language === 'es' ? 'Superadmin creado correctamente' : 'Superadmin created successfully');
      setShowCreate(false);
      setNewAdmin({ email: '', password: '', name: '' });
      fetchSuperadmins();
    } catch (error) {
      console.error('Error creating superadmin:', error);
      const msg = error.response?.data?.detail || (language === 'es' ? 'Error al crear superadmin' : 'Error creating superadmin');
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(
      language === 'es' 
        ? `¿Estás seguro de eliminar a ${userName}? Esta acción no se puede deshacer.`
        : `Are you sure you want to delete ${userName}? This action cannot be undone.`
    )) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/auth/superadmins/${userId}`);
      toast.success(language === 'es' ? 'Superadmin eliminado' : 'Superadmin deleted');
      fetchSuperadmins();
    } catch (error) {
      console.error('Error deleting superadmin:', error);
      const msg = error.response?.data?.detail || (language === 'es' ? 'Error al eliminar' : 'Error deleting');
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="superadmins-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {language === 'es' ? 'Gestión de Superadmins' : 'Superadmin Management'}
          </h1>
          <p className="text-slate-500 mt-1">
            {language === 'es' 
              ? 'Administra los usuarios con acceso total al sistema'
              : 'Manage users with full system access'}
          </p>
        </div>
        
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-superadmin-btn">
              <UserPlus className="w-4 h-4" />
              {language === 'es' ? 'Nuevo Superadmin' : 'New Superadmin'}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {language === 'es' ? 'Crear Superadmin' : 'Create Superadmin'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Nombre' : 'Name'}</Label>
                <Input
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={language === 'es' ? 'Nombre completo' : 'Full name'}
                  data-testid="superadmin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@ejemplo.com"
                  data-testid="superadmin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Contraseña' : 'Password'}</Label>
                <Input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  data-testid="superadmin-password-input"
                />
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  {language === 'es'
                    ? 'Los superadmins tienen acceso total al sistema, incluyendo todas las organizaciones y configuraciones.'
                    : 'Superadmins have full system access, including all organizations and settings.'}
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreate(false)}
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button type="submit" disabled={creating} data-testid="submit-superadmin-btn">
                  {creating ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {language === 'es' ? 'Creando...' : 'Creating...'}
                    </span>
                  ) : (
                    language === 'es' ? 'Crear Superadmin' : 'Create Superadmin'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            {language === 'es' ? 'Superadministradores' : 'Superadministrators'}
            <Badge className="ml-2">{superadmins.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {superadmins.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              {language === 'es' ? 'No hay superadmins' : 'No superadmins found'}
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {superadmins.map((admin) => (
                <div 
                  key={admin.id} 
                  className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                  data-testid={`superadmin-row-${admin.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{admin.name}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {admin.email}
                        </span>
                        {admin.created_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(admin.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge className={admin.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                      {admin.is_active !== false 
                        ? (language === 'es' ? 'Activo' : 'Active')
                        : (language === 'es' ? 'Inactivo' : 'Inactive')}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(admin.id, admin.name)}
                      data-testid={`delete-superadmin-${admin.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Superadmins;
